import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    profile: null,
    classes: [],
    students: [],
    notices: []
  });

  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch teacher profile
        const profileResponse = await api.get(`/teachers/user/${user?._id}`);
        
        // Fetch teacher's classes
        const classesResponse = await api.get(`/classes/teacher/${profileResponse.data.data._id}`);
        
        // Fetch teacher's students
        const studentsResponse = await api.get(`/students/teacher/${profileResponse.data.data._id}`);
        
        // Fetch recent notices
        const noticesResponse = await api.get('/notices');
        
        setDashboardData({
          profile: profileResponse.data.data,
          classes: classesResponse.data.data,
          students: studentsResponse.data.data.slice(0, 5),
          notices: noticesResponse.data.data.slice(0, 5)
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Fallback to mock data if API fails
        setDashboardData({
          profile: {
            name: user?.name || 'Jane Smith',
            employeeId: 'EMP001',
            designation: 'Professor',
            department: 'Computer Science',
            email: user?.email || 'teacher@example.com',
            phone: '+1234567890',
            qualification: 'PhD in Computer Science',
            experience: 10
          },
          classes: [
            { id: 1, subject: 'Mathematics', class: 'Grade 10', section: 'A', students: 35 },
            { id: 2, subject: 'Science', class: 'Grade 9', section: 'B', students: 32 },
            { id: 3, subject: 'Computer Science', class: 'Grade 11', section: 'A', students: 28 }
          ],
          students: [
            { id: 1, name: 'John Doe', roll: '101', class: 'Grade 10', section: 'A' },
            { id: 2, name: 'Jane Smith', roll: '102', class: 'Grade 10', section: 'A' },
            { id: 3, name: 'Robert Johnson', roll: '103', class: 'Grade 10', section: 'A' }
          ],
          notices: [
            { id: 1, title: 'Faculty Meeting', date: '2023-12-15', priority: 'Important' },
            { id: 2, title: 'Exam Schedule', date: '2023-12-10', priority: 'Urgent' }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Teacher Dashboard</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile and Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">My Profile</h2>
              <div className="flex items-center mb-4">
                <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-800">{dashboardData.profile?.name}</h3>
                  <p className="text-gray-600">ID: {dashboardData.profile?.employeeId}</p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Designation:</span> {dashboardData.profile?.designation}</p>
                <p><span className="font-medium">Department:</span> {dashboardData.profile?.department}</p>
                <p><span className="font-medium">Qualification:</span> {dashboardData.profile?.qualification}</p>
                <p><span className="font-medium">Experience:</span> {dashboardData.profile?.experience} years</p>
                <p><span className="font-medium">Email:</span> {dashboardData.profile?.email}</p>
                <p><span className="font-medium">Phone:</span> {dashboardData.profile?.phone}</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <a href="/teacher/profile" className="block w-full text-left bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition">
                  View Profile
                </a>
                <a href="/teacher/classes" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  My Classes
                </a>
                <a href="/teacher/students" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  My Students
                </a>
                <a href="/teacher/attendance" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  Mark Attendance
                </a>
                <a href="/teacher/marks" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  Enter Marks
                </a>
                <a href="/teacher/materials" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  Upload Materials
                </a>
                <a href="/teacher/change-password" className="block w-full text-left bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition">
                  Change Password
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Assigned Classes */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">My Classes</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.classes.map((cls) => (
                      <tr key={cls.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{cls.subject}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{cls.class}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{cls.section}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{cls.students}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <a href={`/teacher/class/${cls.id}`} className="text-primary hover:underline">View</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Students */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Students</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roll</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.students.map((student) => (
                      <tr key={student.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{student.name}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{student.roll}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{student.class}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{student.section}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <a href={`/teacher/student/${student.id}`} className="text-primary hover:underline mr-3">View</a>
                          <a href={`/teacher/student/${student.id}/attendance`} className="text-blue-600 hover:underline">Attendance</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Notices */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Notices</h2>
              <div className="space-y-4">
                {dashboardData.notices.map(notice => (
                  <div key={notice.id} className="border-l-4 border-primary pl-4 py-2">
                    <h3 className="font-medium text-gray-800">{notice.title}</h3>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-gray-600">{notice.date}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        notice.priority === 'Urgent' ? 'bg-red-100 text-red-800' :
                        notice.priority === 'Important' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {notice.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;